import { captureUploadException } from '../common/sentry';

/**
 * Generous enough for the largest thing we upload — a 25 MiB video response
 * on a poor mobile connection — while still bounding a stalled PUT, which
 * would otherwise leave the caller waiting forever.
 */
export const DEFAULT_UPLOAD_TIMEOUT_MS = 10 * 60 * 1000;

export interface SendUploadRequestOptions {
  request: XMLHttpRequest;
  file: File;
  signedUrl: string;
  /** Presign endpoint, reported as context so failures can be grouped. */
  endpoint: string;
  /** Milliseconds. 0 disables the timeout. */
  timeoutMs?: number;
  onProgress?: (fraction: number) => void;
  onSuccess: () => void;
  onFailure: (reason: string) => void;
}

/**
 * Wires and sends one presigned S3 PUT.
 *
 * The app had three separate copies of this wiring and each got it wrong in
 * its own way, which is how uploads were reported as successful when S3 had
 * rejected them. Callers keep their own state shape and supply the two
 * callbacks; everything about when an upload counts as stored lives here.
 */
export function sendUploadRequest({
  request,
  file,
  signedUrl,
  endpoint,
  timeoutMs,
  onProgress,
  onSuccess,
  onFailure
}: SendUploadRequestOptions): void {
  // Bytes sent, on the upload target. Never a completion signal.
  if (onProgress) {
    request.upload.addEventListener('progress', event => {
      if (event.lengthComputable && event.total > 0) {
        onProgress(event.loaded / event.total);
      }
    });
  }

  const fail = (reason: string, status?: number) => {
    // Reported without the file name: participants name their own
    // attachments and a name can identify them.
    captureUploadException(new Error(`Upload failed: ${reason}`), {
      endpoint,
      mimeType: file.type || 'unknown',
      bytes: String(file.size ?? ''),
      ...(status === undefined ? {} : { status: String(status) })
    });
    onFailure(reason);
  };

  // `load` only means the exchange completed. S3 rejecting the PUT is a
  // completed exchange, so the status is what says whether it stored.
  request.addEventListener('load', () => {
    const { status } = request;
    if (status < 200 || status >= 300) {
      fail(`S3 responded ${status}`, status);
      return;
    }
    onSuccess();
  });
  request.addEventListener('error', () => fail('network error'));
  // Aborts are routine: the caller cancels, removes the file, or the component
  // unmounts mid-upload. The caller still needs to know, but it is not a fault
  // and reporting it would bury real failures.
  request.addEventListener('abort', () => onFailure('aborted'));
  request.addEventListener('timeout', () => fail('timed out'));

  request.open('PUT', signedUrl);
  request.setRequestHeader('Content-Type', file.type);
  request.responseType = 'text';
  request.timeout = timeoutMs ?? DEFAULT_UPLOAD_TIMEOUT_MS;
  request.send(file);
}
